import { AlertTriangle, Database, LoaderCircle, SlidersHorizontal } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { ApiError } from '../../api/httpClient'
import { getSongToneRecipe } from '../../api/toneRecipeApi'
import type { ToneRecipeDto, ToneRecipeEffect } from '../../models/toneRecipe'

type KnowledgeAnalysisPageProps = {
  songId: string
}

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded', recipe: ToneRecipeDto }
  | { status: 'empty' }
  | { status: 'error', message: string }

export function formatRecipeConfidence(value: number) {
  if (!Number.isFinite(value)) {
    return '0.0%'
  }

  return `${(Math.max(0, Math.min(1, value)) * 100).toFixed(1)}%`
}

function hasDisplayValue(value: string | number | null | undefined) {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'number') {
    return Number.isFinite(value)
  }

  return value.trim().length > 0 && value !== 'Unknown'
}

function formatToken(value: string) {
  if (value === 'WetDry') {
    return 'Wet/Dry'
  }

  if (value === 'EffectsLoop') {
    return 'FX Loop'
  }

  return value.replace(/([a-z])([A-Z])/g, '$1 $2')
}

function unique(values: Array<string | null | undefined>) {
  const seen = new Set<string>()
  return values.filter((value): value is string => {
    if (value === null || value === undefined || !hasDisplayValue(value)) {
      return false
    }

    const key = value.toLowerCase()
    if (seen.has(key)) {
      return false
    }

    seen.add(key)
    return true
  })
}

function EffectCard({ effect }: { effect: ToneRecipeEffect }) {
  const primaryLabel = hasDisplayValue(effect.canonicalName) ? effect.canonicalName!.trim() : formatToken(effect.effectType)
  const typeLabel = formatToken(effect.effectType)
  const metadata = unique([
    effect.canonicalName ? typeLabel : null,
    effect.brand,
    effect.family,
  ])
  const state = unique([
    effect.status ? formatToken(effect.status) : null,
    effect.enabledState ? formatToken(effect.enabledState) : null,
  ])
  const chain = unique([
    effect.placement ? formatToken(effect.placement) : null,
    effect.routing ? formatToken(effect.routing) : null,
    effect.configuration ? formatToken(effect.configuration) : null,
    effect.order ? `Order ${effect.order}` : null,
  ])

  return <article className="ts-knowledge-effect-card">
    <div className="ts-knowledge-effect-main">
      <span>Effect</span>
      <h3>{primaryLabel}</h3>
      {metadata.length > 0 && <p>{metadata.join(' · ')}</p>}
    </div>
    <div className="ts-knowledge-effect-detail">
      {state.length > 0 && <p>{state.join(' · ')}</p>}
      {chain.length > 0 && <small>{chain.join(' · ')}</small>}
      <strong>Confidence: {formatRecipeConfidence(effect.confidence)}</strong>
    </div>
  </article>
}

function getRecipeEffects(recipe: ToneRecipeDto) {
  return recipe.effects?.effects?.filter(Boolean) ?? []
}

export function KnowledgeAnalysisPage({ songId }: KnowledgeAnalysisPageProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    setState({ status: 'loading' })

    getSongToneRecipe(songId, controller.signal)
      .then((recipe) => {
        if (!recipe) {
          setState({ status: 'empty' })
          return
        }

        setState({ status: 'loaded', recipe })
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return
        }

        if (error instanceof ApiError && error.status === 404) {
          setState({ status: 'empty' })
          return
        }

        setState({ status: 'error', message: 'Unable to load Knowledge Analysis.' })
      })

    return () => controller.abort()
  }, [songId])

  const effects = useMemo(
    () => state.status === 'loaded' ? getRecipeEffects(state.recipe) : [],
    [state],
  )

  return <main className="ts-knowledge-page">
    <section className="container ts-knowledge-shell">
      <div className="ts-knowledge-hero">
        <span className="ts-demo-badge">KNOWLEDGE ANALYSIS</span>
        <div>
          <h1>Knowledge Analysis</h1>
          <p>Every preset is backed by real guitar knowledge.</p>
          <small>Song ID: {songId}</small>
        </div>
      </div>

      {state.status === 'loading' && <section className="ts-knowledge-state" aria-live="polite">
        <LoaderCircle size={22} aria-hidden="true"/>
        <h2>Loading Knowledge Analysis</h2>
        <p>Reading the current Tone Recipe from the Knowledge Pipeline.</p>
      </section>}

      {state.status === 'error' && <section className="ts-knowledge-state error" role="alert">
        <AlertTriangle size={22} aria-hidden="true"/>
        <h2>{state.message}</h2>
        <p>Please try again once the Tone Recipe service is available.</p>
      </section>}

      {state.status === 'empty' && <section className="ts-knowledge-state">
        <Database size={22} aria-hidden="true"/>
        <h2>Tone Recipe not available.</h2>
        <p>This song does not have a structured Knowledge Analysis yet.</p>
      </section>}

      {state.status === 'loaded' && <section className="ts-knowledge-card">
        <div className="ts-knowledge-card-head">
          <SlidersHorizontal size={19} aria-hidden="true"/>
          <div>
            <h2>Effects used</h2>
            <p>Structured effect knowledge from the active Tone Recipe.</p>
          </div>
          <strong>{formatRecipeConfidence(state.recipe.overallConfidence)}</strong>
        </div>

        {effects.length > 0 ? <div className="ts-knowledge-effects-list">
          {effects.map((effect, index) => <EffectCard key={`${effect.effectType}-${effect.canonicalName ?? index}`} effect={effect}/>)}
        </div> : <div className="ts-knowledge-empty">
          <p>No effect knowledge is available for this Tone Recipe yet.</p>
        </div>}
      </section>}
    </section>
  </main>
}
